/**
 * Proposal Card Component
 *
 * Clickable card with donut chart visualization.
 * Click card or + button to select/deselect. + rotates to × when selected.
 *
 * @package oska
 */

/**
 * ProposalCard class
 * Manages a selectable proposal card with chart visualization
 */
class ProposalCard {
  /**
   * @param {HTMLElement} element - The card element with [data-proposal-card]
   */
  constructor(element) {
    this.card = element;
    this.toggle = element.querySelector("[data-toggle]");
    this.chart = element.querySelector("[data-chart]");
    this.isSelected = element.classList.contains("is-selected");

    this.init();
  }

  /**
   * Initialize the component
   */
  init() {
    this.bindEvents();
    this.updateChart();
  }

  /**
   * Bind event listeners
   * Skip when inside ScrollingCards — that component handles click/keyboard.
   */
  bindEvents() {
    if (this.card.closest("[data-scrolling-cards]")) return;

    // Card click (excluding toggle button)
    this.card.addEventListener("click", (e) => {
      // If clicked on toggle or inside toggle, let toggle handle it
      if (
        this.toggle &&
        (e.target === this.toggle || this.toggle.contains(e.target))
      ) {
        return;
      }
      this.toggleSelection();
    });

    // Toggle button click
    if (this.toggle) {
      this.toggle.addEventListener("click", (e) => {
        e.stopPropagation();
        this.toggleSelection();
      });
    }

    // Keyboard support
    this.card.addEventListener("keydown", (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        this.toggleSelection();
      }
    });
  }

  /**
   * Toggle selection state
   */
  toggleSelection() {
    this.isSelected = !this.isSelected;
    this.card.classList.toggle("is-selected", this.isSelected);
    this.emitEvent();
  }

  /**
   * Calculate and apply chart segments based on data attributes
   */
  updateChart() {
    if (!this.chart) return;

    const green = parseFloat(this.chart.dataset.green) || 0;
    const orange = parseFloat(this.chart.dataset.orange) || 0;
    const teal = parseFloat(this.chart.dataset.teal) || 0;
    const coral = parseFloat(this.chart.dataset.coral) || 0;

    const total = green + orange + teal + coral;
    if (total === 0) return;

    // Calculate angles (in degrees, 360 total)
    const greenDeg = (green / total) * 360;
    const orangeDeg = (orange / total) * 360;
    const tealDeg = (teal / total) * 360;

    // Set CSS custom properties for conic-gradient
    this.chart.style.setProperty("--chart-green-end", `${greenDeg}deg`);
    this.chart.style.setProperty(
      "--chart-orange-end",
      `${greenDeg + orangeDeg}deg`,
    );
    this.chart.style.setProperty(
      "--chart-teal-end",
      `${greenDeg + orangeDeg + tealDeg}deg`,
    );
  }

  /**
   * Emit custom event for React integration
   */
  emitEvent() {
    const cardTitle =
      this.card.querySelector(".js-card-title")?.textContent || "";
    const cardId = this.card.dataset.id || "";

    const event = new CustomEvent("proposal-card:toggle", {
      bubbles: true,
      detail: {
        selected: this.isSelected,
        title: cardTitle.trim(),
        id: cardId,
        element: this.card,
      },
    });

    this.card.dispatchEvent(event);
  }

  /**
   * Programmatically select the card
   */
  select() {
    if (!this.isSelected) {
      this.toggleSelection();
    }
  }

  /**
   * Programmatically deselect the card
   */
  deselect() {
    if (this.isSelected) {
      this.toggleSelection();
    }
  }
}

/**
 * Initialize all proposal cards on the page
 * @returns {ProposalCard[]} Array of initialized ProposalCard instances
 */
export default function initProposalCard() {
  const cards = document.querySelectorAll("[data-proposal-card]");
  const instances = [];

  cards.forEach((card) => {
    instances.push(new ProposalCard(card));
  });

  // Demo: Listen for events on styleguide page
  const output = document.getElementById("proposal-card-output");
  if (output) {
    document.addEventListener("proposal-card:toggle", (e) => {
      const { selected, title } = e.detail;
      output.textContent = `Card "${title}" ${selected ? "selected" : "deselected"}`;
    });
  }

  return instances;
}
